import { useEffect, useState, useCallback } from 'react';
import { api } from '../lib/auth';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Switch } from '../components/ui/switch';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import {
  HardDrive, Cloud, CheckCircle2, XCircle, AlertCircle, Loader2,
  PlugZap, Save, Power, Eye, EyeOff,
} from 'lucide-react';
import StorageMigrationCard from '../components/storage/StorageMigrationCard';
import StorageCleanupCard from '../components/storage/StorageCleanupCard';

/**
 * Ayarlar → Depolama (S3 + CloudFront) — Faz 1, Şub 03, 2026
 * --------------------------------------------------------------
 * Admin'in S3 bağlantısını yönetmesi için:
 *  • Toggle ile S3 yazma akışını aç/kapa (kapalıyken local /app/uploads)
 *  • Bucket / region / prefix / CDN / access key / secret giriş
 *  • "Bağlantı Testi" butonu (write→head→get→delete döngüsü)
 *  • Mevcut config görünür, secret maskelenir
 *
 * Faz 2'de upload endpoint'leri bu toggle'a göre yönlenecek; Faz 4'te
 * "Mevcut dosyaları S3'e taşı" butonu eklenecek.
 */
const fmtDate = (iso) => {
  if (!iso) return '—';
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? '—' : d.toLocaleString('tr-TR', { dateStyle: 'short', timeStyle: 'short' });
};

export default function StorageSettings() {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [showSecret, setShowSecret] = useState(false);
  const [form, setForm] = useState({
    enabled: false,
    provider: 's3',
    bucket: 'doping-dwn-spn',
    region: 'eu-central-1',
    prefix: 'dwn/oms',
    cdn_domain: 'd2tfy8ysmefj16.cloudfront.net',
    access_key_id: '',
    secret_access_key: '',
  });
  const [meta, setMeta] = useState({ configured: false, has_secret: false, masked: '', updated_at: null, last_test_ok: null, last_test_at: null });

  const reload = useCallback(async () => {
    try {
      const r = await api.get('/storage/settings');
      const d = r.data || {};
      setMeta({
        configured: d.configured,
        has_secret: d.has_secret,
        masked: d.access_key_id_masked || '',
        updated_at: d.updated_at,
        last_test_ok: d.last_test_ok,
        last_test_at: d.last_test_at,
      });
      if (d.configured) {
        setForm((f) => ({
          ...f,
          enabled: d.enabled,
          bucket: d.bucket || f.bucket,
          region: d.region || f.region,
          prefix: d.prefix || f.prefix,
          cdn_domain: d.cdn_domain || f.cdn_domain,
          access_key_id: '',  // kullanıcı tekrar girer yoksa korunur (UX: maskeli gösterilir)
          secret_access_key: '',
        }));
      }
    } catch (e) {
      toast.error('Ayarlar yüklenemedi: ' + (e.response?.data?.detail || e.message));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { reload(); }, [reload]);

  const setField = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const payload = () => {
    const p = { ...form };
    // Eğer kullanıcı access_key_id boş bırakıp sadece toggle/diğer ayarları değiştiriyorsa,
    // mevcut config gönderilir. Secret boşsa Mongo'dakini koruruz.
    return p;
  };

  const handleTest = async () => {
    if (!form.access_key_id && !meta.configured) {
      toast.error('Access Key ID girin');
      return;
    }
    if (!form.access_key_id && meta.configured) {
      toast.warning("Test için access key id'yi tekrar girin");
      return;
    }
    setTesting(true);
    try {
      const r = await api.post('/storage/settings/test', payload());
      if (r.data?.success) {
        toast.success('Bağlantı başarılı — write/head/get/delete döngüsü OK ✅');
      } else {
        toast.error('Bağlantı başarısız: ' + (r.data?.error || 'bilinmeyen hata'));
      }
    } catch (e) {
      toast.error('Test hatası: ' + (e.response?.data?.detail || e.message));
    } finally {
      setTesting(false);
    }
  };

  const handleSave = async () => {
    if (!form.access_key_id && !meta.configured) {
      toast.error('Önce kimlik bilgilerini girin');
      return;
    }
    setSaving(true);
    try {
      await api.post('/storage/settings', payload());
      toast.success('Ayarlar kaydedildi ve bağlantı doğrulandı ✅');
      await reload();
    } catch (e) {
      toast.error('Kayıt hatası: ' + (e.response?.data?.detail || e.message));
    } finally {
      setSaving(false);
    }
  };

  const handleToggle = async (next) => {
    if (!meta.configured) {
      toast.error('Önce ayarları kaydedin');
      return;
    }
    setSaving(true);
    try {
      await api.post(`/storage/settings/toggle?enabled=${next}`);
      toast.success(next ? 'S3 etkinleştirildi — yeni yüklemeler artık bulut\'a' : 'S3 devre dışı — yeni yüklemeler local diske');
      setField('enabled', next);
      await reload();
    } catch (e) {
      toast.error('Toggle hatası: ' + (e.response?.data?.detail || e.message));
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20 text-slate-400">
        <Loader2 className="h-6 w-6 animate-spin mr-2" /> Ayarlar yükleniyor…
      </div>
    );
  }

  return (
    <div className="space-y-6 max-w-4xl" data-testid="storage-settings-page">
      <div>
        <h1 className="text-2xl font-bold flex items-center gap-2">
          <Cloud className="h-6 w-6 text-sky-500" /> Depolama Ayarları (S3 + CloudFront)
        </h1>
        <p className="text-sm text-slate-500 mt-1">
          Dökümanların (loonbrief, araç, çalışan) S3 bucket&apos;a yüklenmesi için. Toggle kapalıyken yeni yüklemeler local diske (<code className="bg-slate-100 dark:bg-slate-800 px-1 rounded">/app/uploads</code>) gider. Eski kayıtlar kaydedildikleri yerden okunmaya devam eder.
        </p>
      </div>

      {/* Status Card */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base flex items-center gap-2">
            {form.enabled ? <Cloud className="h-4 w-4 text-emerald-500" /> : <HardDrive className="h-4 w-4 text-slate-400" />}
            Mevcut Durum
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div>
              <div className="text-[10px] uppercase text-slate-500 font-semibold">Aktif Backend</div>
              <Badge variant={form.enabled ? 'default' : 'secondary'} className={form.enabled ? 'bg-emerald-500' : ''}>
                {form.enabled ? 'S3 (Cloud)' : 'Local Disk'}
              </Badge>
            </div>
            <div>
              <div className="text-[10px] uppercase text-slate-500 font-semibold">Konfigürasyon</div>
              {meta.configured ? (
                <Badge variant="outline" className="text-emerald-600 border-emerald-200">
                  <CheckCircle2 className="h-3 w-3 mr-1" /> Kayıtlı
                </Badge>
              ) : (
                <Badge variant="outline" className="text-amber-600 border-amber-200">
                  <AlertCircle className="h-3 w-3 mr-1" /> Eksik
                </Badge>
              )}
            </div>
            <div>
              <div className="text-[10px] uppercase text-slate-500 font-semibold">Son Bağlantı Testi</div>
              {meta.last_test_ok === true ? (
                <span className="text-xs text-emerald-600 flex items-center gap-1"><CheckCircle2 className="h-3 w-3" /> {fmtDate(meta.last_test_at)}</span>
              ) : meta.last_test_ok === false ? (
                <span className="text-xs text-rose-600 flex items-center gap-1"><XCircle className="h-3 w-3" /> {fmtDate(meta.last_test_at)}</span>
              ) : <span className="text-xs text-slate-400">—</span>}
            </div>
            <div>
              <div className="text-[10px] uppercase text-slate-500 font-semibold">Son Güncelleme</div>
              <span className="text-xs">{fmtDate(meta.updated_at)}</span>
            </div>
          </div>

          {meta.configured && (
            <div className="mt-4 pt-4 border-t flex items-center justify-between gap-4">
              <div>
                <div className="font-semibold text-sm">S3 Yazma Akışı</div>
                <div className="text-xs text-slate-500">
                  {form.enabled
                    ? 'Yeni yüklemeler doğrudan S3 bucket&apos;a gidiyor.'
                    : 'Şu an local diske yazılıyor. Toggle ile etkinleştirin.'}
                </div>
              </div>
              <Switch
                checked={form.enabled}
                onCheckedChange={handleToggle}
                disabled={saving}
                data-testid="storage-enabled-toggle"
              />
            </div>
          )}
        </CardContent>
      </Card>

      {/* Config Form */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base">Bağlantı Bilgileri</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label>Bucket</Label>
              <Input value={form.bucket} onChange={(e) => setField('bucket', e.target.value)} placeholder="doping-dwn-spn" data-testid="storage-bucket-input" />
            </div>
            <div>
              <Label>Region</Label>
              <Input value={form.region} onChange={(e) => setField('region', e.target.value)} placeholder="eu-central-1" data-testid="storage-region-input" />
            </div>
            <div>
              <Label>Prefix (key path)</Label>
              <Input value={form.prefix} onChange={(e) => setField('prefix', e.target.value)} placeholder="dwn/oms" />
              <p className="text-[10px] text-slate-500 mt-1">Tüm objeler bu prefix altında saklanır. Örn: <code>dwn/oms/loonbrief/2026-01/abc.pdf</code></p>
            </div>
            <div>
              <Label>CloudFront Domain</Label>
              <Input value={form.cdn_domain} onChange={(e) => setField('cdn_domain', e.target.value)} placeholder="d2tfy8ysmefj16.cloudfront.net" />
              <p className="text-[10px] text-slate-500 mt-1">Public URL&apos;ler bu domain ile üretilir.</p>
            </div>
          </div>

          <div className="border-t pt-4 space-y-4">
            <div>
              <Label className="flex items-center justify-between">
                <span>AWS Access Key ID</span>
                {meta.configured && meta.masked && <span className="text-[10px] text-slate-400 font-mono">Mevcut: {meta.masked}</span>}
              </Label>
              <Input
                value={form.access_key_id}
                onChange={(e) => setField('access_key_id', e.target.value)}
                placeholder={meta.configured ? 'Değiştirmek için yeni Access Key ID girin' : 'AKIA...'}
                data-testid="storage-access-key-input"
                autoComplete="off"
              />
            </div>
            <div>
              <Label className="flex items-center justify-between">
                <span>AWS Secret Access Key</span>
                {meta.has_secret && <span className="text-[10px] text-emerald-600">✓ Kayıtlı (boş bırakırsanız korunur)</span>}
              </Label>
              <div className="relative">
                <Input
                  type={showSecret ? 'text' : 'password'}
                  value={form.secret_access_key}
                  onChange={(e) => setField('secret_access_key', e.target.value)}
                  placeholder={meta.has_secret ? 'Değiştirmek için yeni Secret girin' : '•••'}
                  data-testid="storage-secret-input"
                  autoComplete="new-password"
                  className="pr-9"
                />
                <button
                  type="button"
                  onClick={() => setShowSecret(!showSecret)}
                  className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                >
                  {showSecret ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>
            </div>
          </div>

          <div className="flex flex-wrap gap-2 pt-2 border-t">
            <Button onClick={handleTest} disabled={testing || saving} variant="outline" data-testid="storage-test-btn">
              {testing ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <PlugZap className="h-4 w-4 mr-2" />}
              Bağlantı Testi
            </Button>
            <Button onClick={handleSave} disabled={saving || testing} data-testid="storage-save-btn">
              {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Save className="h-4 w-4 mr-2" />}
              {meta.configured ? 'Güncelle' : 'Kaydet'}
            </Button>
            <p className="text-[11px] text-slate-500 self-center ml-2">
              Kayıt sırasında otomatik bağlantı testi yapılır; başarısız olursa kayıt iptal olur.
            </p>
          </div>
        </CardContent>
      </Card>

      {/* Migration */}
      <StorageMigrationCard s3Enabled={form.enabled} />

      {/* Mongo binary cleanup (Faz 5b) */}
      <StorageCleanupCard />
    </div>
  );
}
